'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Card } from '@/components/ui/card';
import { Link } from '@/components/ui/link';
import PlayerAvatar from '@/components/player/PlayerAvatar';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatDate, formatTime } from '@/lib/utils';
import type { MatchWithRating } from '@/components/match/types';

const MAX_SHOWN = 5;

interface NextMatchCardProps {
  /** Upcoming matches, soonest first. Renders nothing when empty. */
  matches: MatchWithRating[];
  locale: string;
}

/** "21 Sep 2026 7:30 pm" — short enough for the one-line eyebrow on phones. */
function formatWhen(iso: string, locale: string, timeZone?: string): string {
  return `${formatDate(iso, locale, timeZone)} ${formatTime(iso, locale, timeZone)}`;
}

function matchContext(match: MatchWithRating): string {
  return [match.competition, match.competitionDivision, match.venue].filter(Boolean).join(' • ');
}

/**
 * The viewer's next scheduled match, shown directly under the profile hero so
 * it is the first thing on screen (it used to sit at the bottom of the
 * sidebar). Further upcoming matches collapse behind a "+N more" toggle.
 */
export default function NextMatchCard({ matches, locale }: NextMatchCardProps) {
  const t = useTranslations('Profile');
  const [expanded, setExpanded] = useState(false);

  // Same 5-match cap the old sidebar list had; a league week can schedule many more.
  const [next, ...rest] = matches.slice(0, MAX_SHOWN);
  if (!next) return null;

  return (
    <Card className="border-primary/40 bg-primary/5 p-4">
      <div className="text-accent mb-2 text-xs font-bold tracking-wider uppercase">
        <span>{t('nextMatch')}</span>
        {' · '}
        {/* `dateISO` is `scheduled_datetime`, a timestamptz — see ProfileHeroCard. */}
        <LocalDateTime iso={next.dateISO} locale={locale} format={formatWhen} />
      </div>
      <div className="flex items-center gap-3">
        <PlayerAvatar name={next.opponent} profilePhoto={next.opponentProfilePhoto} size="md" />
        <div className="min-w-0 flex-1">
          <Link
            href={`/${locale}/user/${next.opponentUsername}`}
            variant="subtle"
            className="text-foreground text-lg font-bold"
            prefetch={false}
          >
            {next.opponent}
          </Link>
          <div className="text-muted text-sm">{matchContext(next)}</div>
        </div>
      </div>

      {rest.length > 0 && (
        <>
          {expanded && (
            <ul id="next-match-more" className="border-border mt-3 space-y-2 border-t pt-3">
              {rest.map((match) => (
                <li key={match.id} className="flex items-center gap-3">
                  <PlayerAvatar
                    name={match.opponent}
                    profilePhoto={match.opponentProfilePhoto}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <Link
                      href={`/${locale}/user/${match.opponentUsername}`}
                      variant="subtle"
                      className="text-sm font-medium"
                      prefetch={false}
                    >
                      {match.opponent}
                    </Link>
                    <div className="text-muted truncate text-xs">{matchContext(match)}</div>
                  </div>
                  <LocalDateTime
                    iso={match.dateISO}
                    locale={locale}
                    format={formatWhen}
                    className="text-muted shrink-0 text-right text-xs"
                  />
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls="next-match-more"
            onClick={() => setExpanded((v) => !v)}
            className="text-accent mt-2 text-xs font-medium hover:underline"
          >
            {expanded ? t('showLess') : t('moreUpcoming', { count: rest.length })}
          </button>
        </>
      )}
    </Card>
  );
}
